import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Typography, Table, Tag, Select, App, Tooltip, Tabs, Badge, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { RobotOutlined, UserOutlined, GlobalOutlined, ApiOutlined, LoadingOutlined, ThunderboltOutlined, HistoryOutlined, AimOutlined, WarningOutlined } from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';

const { Title, Text } = Typography;

interface CheckLog {
  id: string;
  serverKey: string;
  serverName: string;
  checkType: string;
  ip: string;
  state: string;
  prevState: string;
  latency: number;
  triggerType: string;
  triggeredBy: string;
  workerName?: string;
  geoOptimal?: boolean | null;
  workerDistanceKm?: number;
  createdAt: string;
  _inProgress?: boolean;
  _elapsed?: number;
}

interface CheckerStats {
  queueDepth: number;
  activeWorkers: number;
  busyWorkers: number;
  throttled?: boolean;
}

interface ActiveCheckItem {
  serverKey: string;
  serverName?: string;
  ip: string;
  jobType: string;
  startedAt: number;
  workerName?: string;
}

const STATE_COLORS: Record<string, string> = {
  HEALTHY: 'green',
  DEGRADED: 'gold',
  ZOMBIE: 'purple',
  ERROR: 'red',
  DOWN: 'red',
  SUSPECT: 'yellow',
  UNKNOWN: 'default',
};

const CheckLogsPage: React.FC = () => {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [logs, setLogs] = useState<CheckLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // Prefilled from the URL so a link can open the log of one endpoint:
  // /check-logs?serverKey=…&ip=host:port — arriving from a health sparkline.
  const [searchParams] = useSearchParams();
  const [serverKeyFilter, setServerKeyFilter] = useState(searchParams.get('serverKey') || '');
  const [ipFilter, setIpFilter] = useState(searchParams.get('ip') || '');
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [checkerStats, setCheckerStats] = useState<CheckerStats | null>(null);
  const [activeChecks, setActiveChecks] = useState<ActiveCheckItem[]>([]);
  const [sseConnected, setSseConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const sseDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchInFlightRef = useRef(false);

  const [activeTab, setActiveTab] = useState<string>('active');
  const [activePage, setActivePage] = useState(1);
  const [activePageSize, setActivePageSize] = useState(50);

  const fetchCheckerStats = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/health/checker-stats');
      if (data.data) setCheckerStats(data.data);
    } catch {
      // silently ignore
    }
  }, []);

  const fetchActiveChecks = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/health/active-checks');
      setActiveChecks(data.data || []);
    } catch {
      setActiveChecks([]);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: pageSize };
      if (serverKeyFilter) params.serverKey = serverKeyFilter;
      if (ipFilter) params.ip = ipFilter;
      const { data } = await apiClient.get('/check-logs', { params });
      setLogs(data.data.items || []);
      setTotal(data.data.total || 0);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load check logs');
    } finally {
      fetchInFlightRef.current = false;
      setLoading(false);
    }
  }, [page, pageSize, serverKeyFilter, ipFilter, message]);

  const fetchAll = useCallback(() => {
    fetchLogs();
    fetchCheckerStats();
    fetchActiveChecks();
  }, [fetchLogs, fetchCheckerStats, fetchActiveChecks]);

  const debouncedRefresh = useCallback(() => {
    if (sseDebounceRef.current) return;
    sseDebounceRef.current = setTimeout(() => {
      sseDebounceRef.current = null;
      fetchAll();
    }, 3000);
  }, [fetchAll]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      refreshRef.current = setInterval(fetchAll, 15000);
      return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
    }

    const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
    const es = new EventSource(`${baseUrl}/health/stream?token=${token}`);
    eventSourceRef.current = es;

    es.addEventListener('health', () => { debouncedRefresh(); });

    es.onopen = () => {
      setSseConnected(true);
      if (refreshRef.current) { clearInterval(refreshRef.current); refreshRef.current = null; }
    };

    es.onerror = () => {
      setSseConnected(false);
      es.close();
      eventSourceRef.current = null;
      if (!refreshRef.current) {
        refreshRef.current = setInterval(fetchAll, 15000);
      }
    };

    const activeInterval = setInterval(fetchActiveChecks, 5000);
    const statsInterval = setInterval(fetchCheckerStats, 10000);

    return () => {
      es.close();
      eventSourceRef.current = null;
      setSseConnected(false);
      clearInterval(activeInterval);
      clearInterval(statsInterval);
      if (refreshRef.current) clearInterval(refreshRef.current);
      if (sseDebounceRef.current) { clearTimeout(sseDebounceRef.current); sseDebounceRef.current = null; }
    };
  }, [fetchAll, fetchCheckerStats, fetchActiveChecks, debouncedRefresh]);

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  // Build in-progress rows from active checks
  const now = Math.floor(Date.now() / 1000);
  const filteredActiveChecks = activeChecks.filter(
    (ac) => !serverKeyFilter || ac.serverKey === serverKeyFilter
  );
  const inProgressRows: CheckLog[] = filteredActiveChecks.map((ac, i) => ({
    id: `_active_${i}_${ac.serverKey}_${ac.ip}`,
    serverKey: ac.serverKey,
    serverName: ac.serverName || ac.serverKey,
    checkType: ac.jobType || 'ip',
    ip: ac.ip,
    state: 'CHECKING',
    prevState: '',
    latency: 0,
    triggerType: '',
    triggeredBy: '',
    workerName: ac.workerName || '',
    createdAt: new Date(ac.startedAt * 1000).toISOString(),
    _inProgress: true,
    _elapsed: now - ac.startedAt,
  }));

  // Auto-switch to active tab when checks start, to history when idle
  const isChecking = checkerStats && (checkerStats.busyWorkers > 0 || checkerStats.queueDepth > 0);

  // Shared columns for both tabs.
  //
  // Budget: ~1030 CSS px of content on the target screen. Two columns were
  // folded into their neighbours rather than pushed off-screen — Type into the
  // target cell as an icon, Triggered By into the Trigger tag's tooltip.
  const timeColumn: ColumnsType<CheckLog>[number] = {
    title: 'Time',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 150,
    render: (v: string, record: CheckLog) => {
      if (record._inProgress) {
        return (
          <span style={{ fontSize: 12, whiteSpace: 'nowrap', color: token.colorPrimary }}>
            <LoadingOutlined style={{ marginRight: 4 }} />
            {record._elapsed != null && record._elapsed > 0 ? `${record._elapsed}s` : 'just started'}
          </span>
        );
      }
      return <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatTime(v)}</span>;
    },
  };

  const serverColumn: ColumnsType<CheckLog>[number] = {
    title: 'Server',
    dataIndex: 'serverName',
    key: 'serverName',
    ellipsis: { showTitle: false },
    render: (name: string, record: CheckLog) => (
      <Tooltip title={record.serverKey}>
        <Link to={`/servers/${record.serverKey}/health`} style={{ fontSize: 13 }}>
          {name || record.serverKey}
        </Link>
      </Tooltip>
    ),
  };

  // Target = what was checked. The kind of check rides along as an icon: a
  // whole column of the words "Network"/"Proxy" is 90px that the IP needs more.
  const targetColumn: ColumnsType<CheckLog>[number] = {
    title: 'Target',
    dataIndex: 'ip',
    key: 'ip',
    width: 180,
    ellipsis: { showTitle: false },
    render: (ip: string, record: CheckLog) => {
      const isProxy = record.checkType === 'proxy';
      return (
        <Tooltip title={`${isProxy ? 'Proxy' : 'Network'} · ${ip}`}>
          <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
            <Tag
              color={isProxy ? 'purple' : 'cyan'}
              icon={isProxy ? <ApiOutlined /> : <GlobalOutlined />}
              style={{ margin: '0 6px 0 0', padding: '0 4px' }}
            />
            {ip}
          </span>
        </Tooltip>
      );
    },
  };

  const stateColumn: ColumnsType<CheckLog>[number] = {
    title: 'State',
    dataIndex: 'state',
    key: 'state',
    width: 110,
    render: (state: string, record: CheckLog) => {
      if (record._inProgress) {
        return <Tag color="processing" icon={<LoadingOutlined />} style={{ margin: 0 }}>CHECKING</Tag>;
      }
      return <Tag color={STATE_COLORS[state] || 'default'} style={{ margin: 0 }}>{state}</Tag>;
    },
  };

  const workerColumn: ColumnsType<CheckLog>[number] = {
    title: 'Worker',
    dataIndex: 'workerName',
    key: 'workerName',
    width: 130,
    ellipsis: { showTitle: false },
    render: (_v: string, record: CheckLog) => {
      if (!record.workerName) return <Text type="secondary">-</Text>;

      let geoIcon = null;
      if (record.geoOptimal === true) {
        geoIcon = (
          <Tooltip title={`Optimal worker (${record.workerDistanceKm ? Math.round(record.workerDistanceKm).toLocaleString() + ' km' : 'same location'})`}>
            <AimOutlined style={{ color: token.colorSuccess, marginLeft: 4 }} />
          </Tooltip>
        );
      } else if (record.geoOptimal === false) {
        geoIcon = (
          <Tooltip title={`Not closest worker (${record.workerDistanceKm ? Math.round(record.workerDistanceKm).toLocaleString() + ' km' : '?'})`}>
            <WarningOutlined style={{ color: token.colorWarning, marginLeft: 4 }} />
          </Tooltip>
        );
      }

      return (
        <Tooltip title={record.workerName}>
          <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{record.workerName}{geoIcon}</span>
        </Tooltip>
      );
    },
  };

  // Active checks columns (simplified, focused on what's happening now)
  const activeColumns: ColumnsType<CheckLog> = [
    timeColumn,
    serverColumn,
    targetColumn,
    stateColumn,
    workerColumn,
  ];

  // History columns (full detail)
  const historyColumns: ColumnsType<CheckLog> = [
    { ...timeColumn, render: (_v: string, record: CheckLog) => (
      <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatTime(record.createdAt)}</span>
    )},
    serverColumn,
    targetColumn,
    {
      // The transition reads as one fact — "was DEGRADED, now HEALTHY" — so it
      // is one cell, not two columns 100px apart.
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 155,
      render: (state: string, record: CheckLog) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {record.prevState && (
            <Text type="secondary" style={{ fontSize: 12, marginRight: 4 }}>{record.prevState} →</Text>
          )}
          <Tag color={STATE_COLORS[state] || 'default'} style={{ margin: 0 }}>{state}</Tag>
        </span>
      ),
    },
    {
      title: 'Latency',
      dataIndex: 'latency',
      key: 'latency',
      width: 80,
      align: 'right' as const,
      render: (v: number) => (
        <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{v > 0 ? `${v.toFixed(1)} ms` : '—'}</span>
      ),
    },
    {
      title: 'Trigger',
      dataIndex: 'triggerType',
      key: 'triggerType',
      width: 95,
      render: (t: string, record: CheckLog) => (
        <Tooltip title={record.triggeredBy ? `Triggered by ${record.triggeredBy}` : (t === 'manual' ? 'Manual trigger' : 'Scheduled run')}>
          <Tag
            // Neutral on purpose: green/blue here competed with the State tags,
            // the only column where colour should mean something. The icon
            // still tells a manual run from a scheduled one.
            bordered={false}
            icon={t === 'manual' ? <UserOutlined /> : <RobotOutlined />}
            style={{ margin: 0 }}
          >
            {t}
          </Tag>
        </Tooltip>
      ),
    },
    workerColumn,
  ];

  const serverOptions = Array.from(new Set(logs.map((l) => l.serverKey))).map((k) => ({
    label: logs.find((l) => l.serverKey === k)?.serverName || k,
    value: k,
  }));

  return (
    <>
      {/* Title, live checker state and the server filter are one line of text
          between them — so they are one line, not three stacked strips. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        flexWrap: 'wrap', marginBottom: 8,
      }}>
        <Title level={4} style={{ margin: 0 }}>Check Logs</Title>

        {checkerStats && (
          <div style={{
            display: 'flex', gap: 14, fontSize: 12,
            color: token.colorTextSecondary, alignItems: 'center', flexWrap: 'wrap',
          }}>
            <span>
              <span style={{
                display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                backgroundColor: sseConnected ? token.colorSuccess : token.colorWarning,
                marginRight: 4, verticalAlign: 'middle',
              }} />
              <span style={{ verticalAlign: 'middle' }}>{sseConnected ? 'Live' : 'Polling'}</span>
            </span>
            <Tooltip title={`${checkerStats.busyWorkers} of ${checkerStats.activeWorkers} workers processing checks`}>
              <span style={{ cursor: 'help' }}>
                Workers: <span style={{ color: checkerStats.busyWorkers > 0 ? token.colorSuccess : token.colorTextSecondary, fontWeight: 600 }}>
                  {checkerStats.busyWorkers}
                </span>
                <span style={{ color: token.colorTextQuaternary }}>/{checkerStats.activeWorkers}</span>
              </span>
            </Tooltip>
            <Tooltip title={checkerStats.queueDepth > 0 ? `${checkerStats.queueDepth} check jobs waiting in queue` : 'No jobs in queue'}>
              <span style={{ cursor: 'help' }}>
                Queue: <span style={{ color: checkerStats.queueDepth > 0 ? token.colorWarning : token.colorSuccess, fontWeight: 600 }}>
                  {checkerStats.queueDepth}
                </span>
              </span>
            </Tooltip>
            {checkerStats.throttled && (
              <Tooltip title="Workers auto-reduced due to Redis connection pressure.">
                <span style={{ color: token.colorError, fontWeight: 600 }}>!! Throttled</span>
              </Tooltip>
            )}
            {isChecking && !checkerStats.throttled && (
              <Tooltip title="Health checks are actively running">
                <span style={{ color: token.colorSuccess, fontWeight: 600 }}><LoadingOutlined style={{ marginRight: 4 }} />Checking</span>
              </Tooltip>
            )}
            {!isChecking && !checkerStats.throttled && (
              <Tooltip title="Waiting for next scheduled run or manual trigger">
                <span>Idle</span>
              </Tooltip>
            )}
          </div>
        )}

        <div style={{ flex: 1 }} />
        {/* A filter that arrived via the URL (from a health sparkline) must be
            visible, or the operator reads a narrowed log as the whole log. */}
        {ipFilter && (
          <Tag closable style={{ margin: 0 }} onClose={() => { setIpFilter(''); setPage(1); }}>
            IP: {ipFilter}
          </Tag>
        )}
        <Select
          placeholder="Filter by server"
          aria-label="Filter by server"
          allowClear
          size="small"
          value={serverKeyFilter || undefined}
          style={{ width: 220 }}
          onChange={(v) => { setServerKeyFilter(v || ''); setPage(1); setActivePage(1); }}
          options={serverOptions}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="small"
        tabBarStyle={{ marginBottom: 8 }}
        items={[
          {
            key: 'active',
            icon: <ThunderboltOutlined />,
            label: (
              <span>
                Active
                {inProgressRows.length > 0 && (
                  <Badge
                    count={inProgressRows.length}
                    size="small"
                    style={{ marginLeft: 6, backgroundColor: token.colorPrimary }}
                  />
                )}
              </span>
            ),
            children: (
              <Table
                dataSource={inProgressRows}
                columns={activeColumns}
                rowKey="id"
                size="small"
                rowClassName={() => 'check-in-progress-row'}
                locale={{ emptyText: isChecking ? 'Loading active checks...' : 'No active checks — waiting for next cycle' }}
                pagination={inProgressRows.length > activePageSize ? {
                  current: activePage,
                  pageSize: activePageSize,
                  total: inProgressRows.length,
                  size: 'small',
                  showSizeChanger: true,
                  pageSizeOptions: ['25', '50', '100', '200'],
                  onChange: (p, ps) => { setActivePage(p); setActivePageSize(ps); },
                  showTotal: (t) => `${t} active checks`,
                } : false}
              />
            ),
          },
          {
            key: 'history',
            icon: <HistoryOutlined />,
            label: (
              <span>
                History
                {total > 0 && (
                  <Badge
                    count={total}
                    overflowCount={99999}
                    size="small"
                    style={{ marginLeft: 6, backgroundColor: token.colorFill, color: token.colorText, boxShadow: 'none' }}
                  />
                )}
              </span>
            ),
            children: (
              <Table
                dataSource={logs}
                columns={historyColumns}
                rowKey="id"
                loading={loading}
                size="small"
                pagination={{
                  current: page,
                  pageSize,
                  total,
                  size: 'small',
                  showSizeChanger: true,
                  pageSizeOptions: ['25', '50', '100'],
                  onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                }}
              />
            ),
          },
        ]}
      />

      <style>{`
        .check-in-progress-row td {
          background: ${token.colorPrimaryBg} !important;
        }
        .check-in-progress-row:hover td {
          background: ${token.colorPrimaryBgHover} !important;
        }
      `}</style>
    </>
  );
};

export default CheckLogsPage;
